import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  FileSpreadsheet,
  FileText,
  FolderTree,
  ImageUp,
  KeyRound,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { ZmianaKoduGrupyTowarowej } from '@/components/ZmianaKoduGrupyTowarowej';
import { PrzyciskTxt } from '@/components/PrzyciskTxt';
import { BrakujaceObrazkiGrup } from '@/components/BrakujaceObrazkiGrup';
import { PokrycieObrazkowGrup, ZnacznikPokryciaGrupy } from '@/components/PokrycieObrazkowGrup';
import { KafelkiGrupSps } from '@/components/KafelkiGrupSps';
import { ObrazekMini } from '@/components/ObrazekMini';
import { ObrazekTowaru } from '@/components/ObrazekTowaru';
import { fetchPokrycieObrazkow, fetchWysylkiObrazkow, type PokrycieWpis, type WysylkaObrazka } from '@/lib/items-api';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, Field, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { eksportGrupArtykulow } from '@/lib/eksport-grup-artykulow';
import { buildCsvBlob, buildPdfBlob, buildTxtBlob, buildXlsxBlob, downloadBlob, type FormatEksportu } from '@/lib/export';
import {
  checkGroupCode,
  createItemGroup,
  createSubGroup,
  deleteItemGroup,
  fetchDeleteImpact,
  fetchItemGroupTree,
  renameItemGroup,
  type GroupNode,
  type SubGroupNode,
} from '@/lib/item-groups-api';
import { cn } from '@/lib/utils';
import { Link } from 'react-router';

/** Kod grupy pokazujemy monospacem — to klucz naturalny, a nie tekst do czytania. */
const Code = ({ children }: { children: string }) => (
  <span className="font-mono text-xs text-muted-foreground">{children}</span>
);

// ───────────────────────── formularz kodu i nazwy ────────────────────────────

/**
 * Walidacja lustrzana do serwerowej (`item-groups.dto.ts`).
 *
 * Podkreślenie odpada TYLKO w grupie głównej: klucz podgrupy to `'{grupa}_{podgrupa}'`, więc
 * podkreślenie w kodzie grupy odbiera tej wartości jednoznaczność. W kodzie podgrupy jest
 * bezpieczne, bo rozbiór idzie po PIERWSZYM podkreśleniu, a ono należy do grupy nadrzędnej.
 */
const makeSchema = (isSub: boolean) =>
  z.object({
    code: z
      .string()
      .trim()
      .min(1, 'Podaj kod')
      .max(16, 'Najwyżej 16 znaków — SPS obciąłby dłuższy bez ostrzeżenia')
      .refine((v) => isSub || !v.includes('_'), {
        message: 'Kod grupy głównej nie może zawierać podkreślenia — z niego powstaje klucz podgrupy',
      }),
    name: z.string().trim().min(1, 'Podaj nazwę').max(32, 'Najwyżej 32 znaki (DESCR w SPS ma 32, nie 255)'),
  });

type FormValues = { code: string; name: string };

function GroupModal({
  groupId,
  parent,
  onClose,
  onSaved,
}: {
  groupId: string;
  /** Podany = tworzymy PODGRUPĘ w tej grupie. */
  parent: GroupNode | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);
  const isSub = parent !== null;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(makeSchema(isSub)), defaultValues: { code: '', name: '' } });

  const typed = watch('code')?.trim() ?? '';
  const check = useQuery({
    queryKey: ['item-groups', 'check-code', groupId, parent?.id ?? '', typed],
    queryFn: () => checkGroupCode(groupId, typed, parent?.id),
    enabled: typed.length > 0,
  });

  const onSubmit = async (v: FormValues) => {
    setServerError(null);
    try {
      if (parent) {
        const res = await createSubGroup(parent.id, groupId, v.code, v.name);
        toast.success(`${t('igrp.subCreated')} (${res.isgid})`);
      } else {
        const res = await createItemGroup(groupId, v.code, v.name);
        toast.success(`${t('igrp.created')} (${res.code})`);
      }
      onSaved();
      onClose();
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : t('common.error'));
    }
  };

  const c = check.data;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={isSub ? t('igrp.addSubTitle') : t('igrp.addTitle')}
        // przy grupie głównej BEZ opisu: ta sama podpowiedź stoi już pod polem kodu,
        // a powtórzona dwa razy w jednym modalu wygląda na pomyłkę
        description={isSub ? `${t('igrp.inGroup')}: ${parent.name} (${parent.code})` : undefined}
      >
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormGrid>
            <Field
              label={isSub ? t('igrp.subCode') : t('igrp.code')}
              htmlFor="code"
              required
              error={errors.code?.message}
              hint={isSub ? t('igrp.subCodeHint') : t('igrp.codeHint')}
            >
              <Input id="code" maxLength={16} autoComplete="off" className="font-mono" {...register('code')} />
            </Field>

            {/* Podglądowa wartość ZŁOŻONEGO klucza. Bez tego użytkownik wpisuje kod podgrupy
                i nie wie, że do SPS pojedzie coś innego niż to, co widzi w polu. */}
            {isSub && c?.isgid && (
              <p className="rounded-md border border-border bg-muted/40 p-2.5 text-xs text-muted-foreground">
                {t('igrp.isgidPreview')} <span className="font-mono text-foreground">{c.isgid}</span>
                <br />
                {t('igrp.isgidWhy')}
              </p>
            )}

            {typed.length > 0 && (
              <p className="text-xs">
                {check.isPending ? (
                  <span className="text-muted-foreground">
                    <Loader2 className="mr-1 inline size-3 animate-spin" />
                    {t('common.loading')}
                  </span>
                ) : c?.free ? (
                  <span className="text-[var(--success,#16a34a)]">{t('igrp.codeFree')}</span>
                ) : c?.takenBy ? (
                  <span className="text-destructive">
                    {t('igrp.codeTaken').replace('{name}', c.takenBy.name).replace('{code}', c.takenBy.code)}
                  </span>
                ) : c?.isgidTakenBy ? (
                  // kolizja SAMEGO klucza złożonego przy wolnym kodzie — możliwa, bo ISGID
                  // powstaje ze zlepienia dwóch kodów
                  <span className="text-destructive">
                    {t('igrp.isgidTaken').replace('{name}', c.isgidTakenBy.name)}
                  </span>
                ) : null}
                {c?.pending && <span className="ml-2 text-[var(--warning)]">{t('igrp.codePending')}</span>}
              </p>
            )}

            {/* Kod, który już kiedyś usuwano. Kaskada czyści teraz wpisy dostępu, ale TYLKO te
                znane read-modelowi i dopiero po zastosowaniu operacji — więc ostrzeżenie zostaje,
                zmienia się tylko jego powód. */}
            {c?.previouslyDeleted && (
              <p className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2.5 text-xs">
                {t('igrp.codeReused')}
              </p>
            )}

            <Field label={t('igrp.name')} htmlFor="name" required error={errors.name?.message} hint={t('igrp.nameHint')}>
              <Input id="name" maxLength={32} {...register('name')} />
            </Field>

            <ModalError>{serverError}</ModalError>
          </FormGrid>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting || c?.free === false}>
              {isSubmitting && <Loader2 className="animate-spin" />} {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RenameModal({
  groupId,
  node,
  onClose,
  onSaved,
}: {
  groupId: string;
  node: { id: string; code: string; name: string; isgid: string | null; isSub: boolean };
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<{ name: string }>({
    resolver: zodResolver(makeSchema(true).pick({ name: true })),
    defaultValues: { name: node.name },
  });

  const onSubmit = async (v: { name: string }) => {
    setServerError(null);
    try {
      await renameItemGroup(node.id, groupId, v.name);
      toast.success(t('igrp.renamed'));
      onSaved();
      onClose();
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : t('common.error'));
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={t('igrp.renameTitle')}
        description={`${node.isSub ? t('igrp.subGroup') : t('igrp.group')}: ${node.isgid ?? node.code}`}
      >
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormGrid>
            <Field label={t('igrp.name')} htmlFor="rname" required error={errors.name?.message}>
              <Input id="rname" maxLength={32} {...register('name')} />
            </Field>

            {/* Dlaczego kodu NIE MA w tym formularzu — inaczej brak pola wygląda na przeoczenie. */}
            <p className="rounded-md border border-border bg-muted/40 p-2.5 text-xs text-muted-foreground">
              {t('igrp.codeImmutable')}
            </p>

            <ModalError>{serverError}</ModalError>
          </FormGrid>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="animate-spin" />} {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Usunięcie grupy albo podgrupy — pytanie Z TREŚCIĄ.
 *
 * Kaskada rusza cztery tabele, więc modal najpierw POKAZUJE, co zniknie, a dopiero potem pyta.
 * Bez tego „usuń" byłoby pytaniem, na które nie da się odpowiedzialnie odpowiedzieć — nikt nie
 * zgadnie, że grupa zabiera ze sobą reguły limitów.
 */
function DeleteModal({
  groupId,
  nodeId,
  onClose,
  onSaved,
}: {
  groupId: string;
  nodeId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ack, setAck] = useState(false);

  const impact = useQuery({
    queryKey: ['item-groups', 'delete-impact', nodeId, groupId],
    queryFn: () => fetchDeleteImpact(nodeId, groupId),
  });

  const d = impact.data;

  const onConfirm = async () => {
    setServerError(null);
    setBusy(true);
    try {
      await deleteItemGroup(nodeId, groupId);
      toast.success(t('igrp.deleted'));
      onSaved();
      onClose();
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={d?.node.isSubGroup ? t('igrp.deleteSubTitle') : t('igrp.deleteTitle')}
        description={d ? `${d.node.name} (${d.node.isgid ?? d.node.code})` : undefined}
      >
        {impact.isPending || !d ? (
          <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
          </p>
        ) : (
          <FormGrid>
            <ul className="space-y-1.5 text-sm">
              {/* Towary są ODPINANE, nie usuwane — to najczęstsze nieporozumienie przy kasowaniu
                  grupy, więc mówimy to wprost i osobno dla podgrupy. */}
              <li>
                <span className="font-semibold tabular-nums">{d.counts.items}</span>{' '}
                {d.node.isSubGroup
                  ? t('igrp.delItemsToParent').replace('{code}', d.node.fallbackGroupCode ?? '—')
                  : t('igrp.delItemsDetached')}
              </li>
              {!d.node.isSubGroup && (
                <>
                  <li>
                    <span className="font-semibold tabular-nums">{d.counts.subGroups}</span>{' '}
                    {t('igrp.delSubGroups')}
                    {d.subGroups.length > 0 && (
                      <span className="ml-1 font-mono text-xs text-muted-foreground">
                        {d.subGroups.map((s) => s.isgid ?? s.code).join(', ')}
                      </span>
                    )}
                  </li>
                  <li className={d.counts.rationing > 0 ? 'text-[var(--warning)]' : undefined}>
                    <span className="font-semibold tabular-nums">{d.counts.rationing}</span>{' '}
                    {t('igrp.delRationing')}
                  </li>
                </>
              )}
              {/* Wpisy dostępu. Świadomie NIE piszemy „odbierze dostęp": bez flag
                  REST_ENABLED/REST_DENY nie wiadomo, czy wpis zezwalał, czy ZAKAZYWAŁ. */}
              <li className={d.counts.restrictions > 0 ? 'text-[var(--warning)]' : undefined}>
                <span className="font-semibold tabular-nums">{d.counts.restrictions}</span>{' '}
                {t('igrp.delRestrictions')}
              </li>
            </ul>

            {d.pending && (
              <p className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2.5 text-xs">
                {t('igrp.delPendingWarn')}
              </p>
            )}

            {/* Luka, której NIE WOLNO schować: kaskada nie obejmuje wpisów dostępu. */}
            <label className="flex cursor-pointer items-start gap-2 rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2.5 text-xs">
              <input
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
                className="mt-0.5 shrink-0"
              />
              <span>{t('igrp.delAssumedKeyAck')}</span>
            </label>

            <ModalError>{serverError}</ModalError>
          </FormGrid>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" variant="destructive" disabled={!d || !ack || busy} onClick={onConfirm}>
            {busy && <Loader2 className="animate-spin" />} {t('igrp.deleteConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────── licznik przy węźle ──────────────────────────

const Count = ({ label, value, warn }: { label: string; value: number; warn?: boolean }) => (
  <span className="whitespace-nowrap text-xs text-muted-foreground">
    {label} <span className={cn('font-semibold tabular-nums', warn ? 'text-[var(--warning)]' : 'text-foreground')}>{value}</span>
  </span>
);

// ─────────────────────────────────── ekran ───────────────────────────────────

export function ItemGroups() {
  const { t } = useI18n();
  const { island } = useIslands();
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.role === 'ADMIN';
  const groupId = island?.id ?? '';

  const [search, setSearch] = useState('');
  const [includeEmpty, setIncludeEmpty] = useState(true);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [addingSubTo, setAddingSubTo] = useState<GroupNode | null>(null);
  const [kodFor, setKodFor] = useState<{ id: string; code: string; name: string } | null>(null);
  const [renaming, setRenaming] = useState<{
    id: string;
    code: string;
    name: string;
    isgid: string | null;
    isSub: boolean;
  } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  /**
   * OBRAZEK GRUPY (27.10.2026) — okno zdjęcia artykułu z celem w `ItemGroups`/`ItemSubGroups`.
   * `klucz` to wiersz SPS: kod grupy głównej albo ISGID podgrupy — po nim szukamy trwającej wysyłki.
   */
  const [obrazekFor, setObrazekFor] = useState<{
    id: string;
    nazwa: string;
    kod: string;
    klucz: string;
    imageFile: string | null;
    hasImage: boolean;
  } | null>(null);
  /** Podgląd skutków zmiany kodu — TYLKO grupa główna; kod podgrupy to inna kaskada (ISGID). */

  /**
   * Kiedy zlecono zgranie obrazków grup z automatu. Pliki dojeżdżają w cyklu agenta, więc przez
   * dziesięć minut odpytujemy drzewo co 15 s — inaczej miniatury zostałyby puste do odświeżenia.
   */
  const [zgranieOd, setZgranieOd] = useState<number | null>(null);
  const tree = useQuery({
    queryKey: ['item-groups', groupId, search, includeEmpty],
    queryFn: () => fetchItemGroupTree(groupId, search, includeEmpty),
    enabled: groupId !== '',
    refetchInterval: () => (zgranieOd !== null && Date.now() - zgranieOd < 10 * 60_000 ? 15_000 : false),
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: ['item-groups'] });
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const s = tree.data?.summary;
  const groups = tree.data?.groups ?? [];
  /**
   * POKRYCIE KAFELKÓW NA AUTOMATACH (29.10.2026) — ten sam endpoint i ten sam klucz pamięci co okno
   * „Zarządzaj zdjęciami", więc spis zlecony w jednym miejscu widać w drugim. Pytamy tylko, gdy jakaś
   * grupa ma obrazek: bez tego nie ma czego sprawdzać.
   */
  const maObrazki = groups.some((g) => g.imageFile || g.subGroups.some((sub) => sub.imageFile));
  const pokrycie = useQuery({
    queryKey: ['pokrycie-obrazkow', groupId],
    queryFn: () => fetchPokrycieObrazkow(groupId),
    enabled: groupId !== '' && isAdmin && maObrazki,
    refetchInterval: (q) => (q.state.data?.maszyny.some((m) => m.wToku) === true ? 10_000 : false),
  });
  const pokrycieGrupy = useMemo(() => {
    const m = new Map<string, PokrycieWpis>();
    for (const p of pokrycie.data?.grupy ?? []) m.set(p.towar.id, p);
    return m;
  }, [pokrycie.data]);

  /** Grupy i podgrupy wskazujące plik, którego biblioteka portalu nie ma. */
  const bezPliku = groups.reduce(
    (n, g) =>
      n +
      (g.imageFile && !g.hasImage ? 1 : 0) +
      g.subGroups.filter((sub) => sub.imageFile && !sub.hasImage).length,
    0,
  );

  /** Trwające wysyłki obrazków grup — znacznik „2/3" przy grupie, jak przy artykule. */
  const wysylki = useQuery({
    queryKey: ['obrazki-wysylki', groupId],
    queryFn: () => fetchWysylkiObrazkow(groupId),
    enabled: groupId !== '' && isAdmin,
    refetchInterval: (q) => ((q.state.data?.wysylki.length ?? 0) > 0 ? 15_000 : false),
  });
  const wysylkaGrupy = useMemo(() => {
    const m = new Map<string, WysylkaObrazka>();
    for (const w of wysylki.data?.wysylki ?? []) for (const g of w.grupy) m.set(g.klucz.trim().toLowerCase(), w);
    return m;
  }, [wysylki.data]);

  /** Przycisk obrazka i znacznik wysyłki — ten sam dla grupy i podgrupy. */
  const przyciskObrazka = (cel: NonNullable<typeof obrazekFor>) => {
    const w = wysylkaGrupy.get(cel.klucz.trim().toLowerCase());
    const blad = w !== undefined && w.bledy.length > 0;
    return (
      <>
        {w !== undefined && (
          <button
            type="button"
            className="self-center"
            title={
              blad
                ? t('obraz.lista.blad').replace('{automaty}', w.bledy.map((b) => b.automat).join(', '))
                : t('obraz.lista.czeka').replace('{automaty}', w.czekaNa.join(', '))
            }
            onClick={() => setObrazekFor(cel)}
          >
            <Badge variant={blad ? 'destructive' : 'warning'}>{blad ? t('obraz.lista.bladKrotko') : `${w.gotowe}/${w.razem}`}</Badge>
          </button>
        )}
        <Button
          variant="ghost"
          size="icon"
          title={t('igrp.obrazek')}
          aria-label={`${t('igrp.obrazek')} ${cel.nazwa}`}
          onClick={() => setObrazekFor(cel)}
        >
          <ImageUp />
        </Button>
      </>
    );
  };

  const toast = useToast();
  const [exporting, setExporting] = useState<FormatEksportu | null>(null);
  /**
   * EKSPORT — ten sam pasek co na liście artykułów (CSV, TXT, XLSX, PDF), dane z `lib/eksport-grup-artykulow.ts`.
   * Bez okna zajętości (`reportKey`): plik powstaje z drzewa już wczytanego na ekran, bez zapytań do bazy.
   */
  const runExport = async (kind: FormatEksportu, naglowki = true) => {
    setExporting(kind);
    try {
      const { cols, rows } = eksportGrupArtykulow(groups, t);
      if (rows.length === 0) {
        toast.error(t('table.exportEmpty'));
        return;
      }
      const plik = 'grupy-artykulow';
      const tytul = t('nav.itemGroups');
      if (kind === 'csv') downloadBlob(buildCsvBlob(cols, rows), `${plik}.csv`);
      if (kind === 'txt') downloadBlob(buildTxtBlob(cols, rows, naglowki), `${plik}.txt`);
      if (kind === 'xlsx') downloadBlob(await buildXlsxBlob(cols, rows, tytul), `${plik}.xlsx`);
      if (kind === 'pdf') downloadBlob(await buildPdfBlob(cols, rows, tytul), `${plik}.pdf`);
      toast.success(t('table.exportDone'));
    } catch (err) {
      toast.error(`${t('table.exportFailed')}: ${(err as Error).message}`);
    } finally {
      setExporting(null);
    }
  };

  // przy szukaniu rozwijamy wszystko, co ma dopasowane podgrupy — inaczej trafienie
  // w podgrupę byłoby schowane pod zwiniętym rodzicem
  const expanded = useMemo(
    () => (search ? new Set(groups.filter((g) => g.subGroups.length > 0).map((g) => g.id)) : open),
    [search, groups, open],
  );

  const kpis = s
    ? [
        { label: t('igrp.kpi.groups'), value: s.groups },
        { label: t('igrp.kpi.subGroups'), value: s.subGroups },
        { label: t('igrp.kpi.itemsInSubGroups'), value: s.itemsInSubGroups },
        { label: t('igrp.kpi.itemsWithoutGroup'), value: s.itemsWithoutGroup, hint: t('igrp.noGroupHint') },
        { label: t('igrp.kpi.emptyGroups'), value: s.emptyGroups, hint: t('igrp.emptyHint') },
        {
          label: t('igrp.kpi.rationing'),
          value: s.rationingOnGroups,
          warn: s.rationingOrphans > 0,
          hint: s.rationingOrphans > 0 ? t('igrp.orphanHint').replace('{n}', String(s.rationingOrphans)) : t('igrp.rationingHint'),
        },
      ]
    : [];

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('nav.itemGroups')}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={refresh} aria-label={t('table.refresh')}>
            <RefreshCw className={cn(tree.isFetching && 'animate-spin')} />
          </Button>
          {isAdmin && (
            <Button onClick={() => setAdding(true)}>
              <Plus /> {t('igrp.add')}
            </Button>
          )}
        </div>
      </div>

      {s && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {kpis.map((k) => (
            <div key={k.label} title={k.hint} className="rounded-md border border-border bg-card p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k.label}</div>
              <div className={cn('mt-0.5 text-xl font-semibold tabular-nums', k.warn && 'text-[var(--warning)]')}>
                {k.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/*
        ⚠️ BEZ AKAPITU O DWÓCH POZIOMACH SPS — zgłoszone wprost (28.08.2026). Model widać na samym
        drzewie (grupa → podgrupa i nic głębiej), a skutek dla limitów wychodzi w Uprawnieniach,
        gdzie podgrupa ma własny napis „limit niedostępny (podgrupa)".
      */}

      {s && s.rationingOrphans > 0 && (
        <p className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2.5 text-xs">
          {t('igrp.orphanNotice').replace('{n}', String(s.rationingOrphans))}
        </p>
      )}

      {isAdmin && groupId && <KafelkiGrupSps groupId={groupId} />}
      {isAdmin && groupId && (
        <BrakujaceObrazkiGrup groupId={groupId} brakuje={bezPliku} onZlecono={() => setZgranieOd(Date.now())} />
      )}
      {isAdmin && groupId && pokrycie.data && pokrycie.data.grupy.length > 0 && (
        <PokrycieObrazkowGrup groupId={groupId} dane={pokrycie.data} />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('igrp.searchPlaceholder')}
          className="h-9 w-72"
        />
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input type="checkbox" checked={includeEmpty} onChange={(e) => setIncludeEmpty(e.target.checked)} />
          {t('igrp.showEmpty')}
        </label>

        {/* ⚠️ KOLEJNOŚĆ CSV, TXT, XLSX, PDF jak na każdym pasku eksportu — pilnuje `formaty-eksportu.spec.ts`. */}
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void runExport('csv')}
            disabled={exporting !== null || groups.length === 0}
            data-export="csv"
            title="CSV"
          >
            {exporting === 'csv' ? <Loader2 className="animate-spin" /> : <Download />}
            CSV
          </Button>
          <PrzyciskTxt
            disabled={exporting !== null || groups.length === 0}
            pracuje={exporting === 'txt'}
            onWybor={(naglowki) => void runExport('txt', naglowki)}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => void runExport('xlsx')}
            disabled={exporting !== null || groups.length === 0}
            data-export="xlsx"
            title="XLSX"
          >
            {exporting === 'xlsx' ? <Loader2 className="animate-spin" /> : <FileSpreadsheet />}
            XLSX
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void runExport('pdf')}
            disabled={exporting !== null || groups.length === 0}
            data-export="pdf"
            title="PDF"
          >
            {exporting === 'pdf' ? <Loader2 className="animate-spin" /> : <FileText />}
            PDF
          </Button>
        </div>
      </div>

      {tree.isPending ? (
        <Card>
          <CardContent className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
          </CardContent>
        </Card>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">{t('igrp.empty')}</CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {groups.map((g) => {
            const isOpen = expanded.has(g.id);
            return (
              <Card key={g.id}>
                <CardContent className="p-3">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <button
                      type="button"
                      onClick={() => toggle(g.id)}
                      disabled={g.subGroups.length === 0}
                      aria-expanded={isOpen}
                      aria-label={isOpen ? t('igrp.collapse') : t('igrp.expand')}
                      className="rounded p-0.5 text-muted-foreground enabled:hover:bg-accent disabled:opacity-30"
                    >
                      {isOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                    </button>
                    {/* Kafelek grupy w SPSUI zamiast ikony folderu, gdy grupa go ma. */}
                    {(g.imageFile ?? null) !== null ? (
                      <ObrazekMini groupId={groupId} nazwaPliku={g.imageFile ?? null} hasImage={g.hasImage ?? false} rozmiar={32} />
                    ) : (
                      <FolderTree className="size-4 shrink-0 text-muted-foreground" />
                    )}
                    {/*
                      ⚠️ NAZWA GRUPY PROWADZI DO ARTYKUŁÓW TEJ GRUPY (zgłoszone 01.09.2026). Wcześniej
                      trzeba było przejść na Artykuły i wybrać tę samą grupę z listy rozwijanej —
                      czyli zrobić drugi raz to, co się właśnie kliknęło.

                      ⚠️ STRZAŁKA ROZWIJANIA ZOSTAJE OSOBNYM PRZYCISKIEM (obok), bo to dwie różne
                      czynności: „pokaż podgrupy tutaj" i „wyjdź do listy towarów".
                    */}
                    <Link
                      to={`/produkty?grupa=${encodeURIComponent(g.id)}`}
                      className="font-medium underline-offset-4 hover:underline"
                      title={t('igrp.doArtykulow')}
                    >
                      {g.name}
                    </Link>
                    <Code>{g.code}</Code>
                    {isAdmin && (
                      <ZnacznikPokryciaGrupy
                        wpis={pokrycieGrupy.get(g.id)}
                        onClick={() =>
                          setObrazekFor({
                            id: g.id,
                            nazwa: g.name,
                            kod: g.code,
                            klucz: g.code,
                            imageFile: g.imageFile ?? null,
                            hasImage: g.hasImage ?? false,
                          })
                        }
                      />
                    )}
                    {g.pending && (
                      <Badge variant="neutral" title={t('igrp.pendingHint')}>
                        <Clock className="mr-1 size-3" />
                        {t('igrp.pending')}
                      </Badge>
                    )}

                    <span className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1">
                      {/* „bezpośrednio" i „z podgrupami" osobno: przy grupie z podgrupami to dwie
                          różne liczby i zlanie ich w jedną znaczyłoby raz jedno, raz drugie. */}
                      <Count label={t('igrp.itemsDirect')} value={g.items} />
                      {g.subGroups.length > 0 && <Count label={t('igrp.itemsSubtree')} value={g.subtreeItems} />}
                      <Count label={t('igrp.machines')} value={g.subtreeMachines} />
                      <Count label={t('igrp.rationing')} value={g.rationingRules} />
                      {isAdmin && (
                        <span className="flex gap-1">
                          {przyciskObrazka({
                            id: g.id,
                            nazwa: g.name,
                            kod: g.code,
                            klucz: g.code,
                            imageFile: g.imageFile ?? null,
                            hasImage: g.hasImage ?? false,
                          })}
                          <Button
                            variant="ghost"
                            size="icon"
                            title={t('igrp.addSub')}
                            aria-label={`${t('igrp.addSub')} ${g.name}`}
                            onClick={() => setAddingSubTo(g)}
                          >
                            <Plus />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title={t('igrp.renameTitle')}
                            aria-label={`${t('igrp.renameTitle')} ${g.name}`}
                            onClick={() =>
                              setRenaming({ id: g.id, code: g.code, name: g.name, isgid: null, isSub: false })
                            }
                          >
                            <Pencil />
                          </Button>
                          {/* ⚠️ Kod to KLUCZ, nazwa to opis — osobny przycisk, bo osobna waga operacji. */}
                          <Button
                            variant="ghost"
                            size="icon"
                            title={t('ig.codeChangeAction')}
                            aria-label={`${t('ig.codeChangeAction')} ${g.name}`}
                            onClick={() => setKodFor({ id: g.id, code: g.code, name: g.name })}
                          >
                            <KeyRound />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title={t('igrp.deleteTitle')}
                            aria-label={`${t('igrp.deleteTitle')} ${g.name}`}
                            onClick={() => setDeleting(g.id)}
                          >
                            <Trash2 />
                          </Button>
                        </span>
                      )}
                    </span>
                  </div>

                  {g.subtreeItems === 0 && (
                    <p className="mt-2 pl-7 text-xs text-muted-foreground">{t('igrp.emptyGroup')}</p>
                  )}

                  {isOpen && g.subGroups.length > 0 && (
                    <ul className="mt-2 space-y-1.5 border-l border-border pl-4 ml-3">
                      {g.subGroups.map((sub: SubGroupNode) => (
                        <li key={sub.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-3">
                          {(sub.imageFile ?? null) !== null && (
                            <ObrazekMini groupId={groupId} nazwaPliku={sub.imageFile ?? null} hasImage={sub.hasImage ?? false} rozmiar={28} />
                          )}
                          <span className="text-sm">{sub.name}</span>
                          {/* Klucz podgrupy to ZŁOŻONA wartość — pokazujemy ją, a nie goły kod,
                              bo to ona jedzie do Items.ITEMSUBGROUP. */}
                          <Code>{sub.isgid ?? sub.code}</Code>
                          {isAdmin && sub.isgid !== null && (
                            <ZnacznikPokryciaGrupy
                              wpis={pokrycieGrupy.get(sub.id)}
                              onClick={() =>
                                setObrazekFor({
                                  id: sub.id,
                                  nazwa: sub.name,
                                  kod: sub.isgid!,
                                  klucz: sub.isgid!,
                                  imageFile: sub.imageFile ?? null,
                                  hasImage: sub.hasImage ?? false,
                                })
                              }
                            />
                          )}
                          {sub.isgid === null && (
                            <Badge variant="warning" title={t('igrp.noIsgidHint')}>
                              {t('igrp.noIsgid')}
                            </Badge>
                          )}
                          {sub.pending && (
                            <Badge variant="neutral" title={t('igrp.pendingHint')}>
                              <Clock className="mr-1 size-3" />
                              {t('igrp.pending')}
                            </Badge>
                          )}
                          <span className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1">
                            <Count label={t('igrp.itemsDirect')} value={sub.items} />
                            <Count label={t('igrp.machines')} value={sub.machines} />
                            {isAdmin && (
                              <span className="flex gap-1">
                                {/* ⚠️ Podgrupa bez ISGID nie ma czym wskazać wiersza w SPS — przycisku nie ma. */}
                                {sub.isgid !== null &&
                                  przyciskObrazka({
                                    id: sub.id,
                                    nazwa: sub.name,
                                    kod: sub.isgid,
                                    klucz: sub.isgid,
                                    imageFile: sub.imageFile ?? null,
                                    hasImage: sub.hasImage ?? false,
                                  })}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title={t('igrp.renameTitle')}
                                  aria-label={`${t('igrp.renameTitle')} ${sub.name}`}
                                  onClick={() =>
                                    setRenaming({
                                      id: sub.id,
                                      code: sub.code,
                                      name: sub.name,
                                      isgid: sub.isgid,
                                      isSub: true,
                                    })
                                  }
                                >
                                  <Pencil />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title={t('igrp.deleteSubTitle')}
                                  aria-label={`${t('igrp.deleteSubTitle')} ${sub.name}`}
                                  onClick={() => setDeleting(sub.id)}
                                >
                                  <Trash2 />
                                </Button>
                              </span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {adding && groupId && (
        <GroupModal groupId={groupId} parent={null} onClose={() => setAdding(false)} onSaved={refresh} />
      )}
      {addingSubTo && groupId && (
        <GroupModal
          groupId={groupId}
          parent={addingSubTo}
          onClose={() => setAddingSubTo(null)}
          onSaved={refresh}
        />
      )}
      {kodFor && groupId && (
        <ZmianaKoduGrupyTowarowej
          grupaId={kodFor.id}
          islandId={groupId}
          obecnyKod={kodFor.code}
          nazwa={kodFor.name}
          onClose={() => setKodFor(null)}
          onSaved={refresh}
        />
      )}
      {renaming && groupId && (
        <RenameModal groupId={groupId} node={renaming} onClose={() => setRenaming(null)} onSaved={refresh} />
      )}
      {deleting && groupId && (
        <DeleteModal groupId={groupId} nodeId={deleting} onClose={() => setDeleting(null)} onSaved={refresh} />
      )}
      {obrazekFor && groupId && (
        <ObrazekTowaru
          grupaId={obrazekFor.id}
          islandId={groupId}
          numer={obrazekFor.kod}
          nazwa={obrazekFor.nazwa}
          indeks={null}
          obecny={obrazekFor.imageFile}
          maBajty={obrazekFor.hasImage}
          wysylka={wysylkaGrupy.get(obrazekFor.klucz.trim().toLowerCase()) ?? null}
          onClose={() => setObrazekFor(null)}
        />
      )}
    </div>
  );
}
